import React, { useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import {
	combineExtensionProviders,
	type ExtensionProvider,
} from '@atlaskit/editor-common/extensions';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { CardClient, SmartCardProvider } from '@atlaskit/link-provider';
import { RendererWithAnalytics as AkRenderer } from '@atlaskit/renderer';
import { ExtensionMode, HubRendererWrapper } from '@atlassian/company-hub-common';
import { getSectionExtensionContents } from '@atlassian/hub-providers';
import { sectionExtensionProvider } from '@atlassian/section-extension';

import { useHubExtensionHandlers } from '../../controllers/use-hub-extension-handlers';

import type { HubRendererProps } from './types';

export const HubRenderer = (props: HubRendererProps): React.JSX.Element => {
	const {
		product,
		additionalExtensionHandlers = {},
		PropsProvider,
		contentId,
		dataProviders,
	} = props;
	const [sectionsProvider, setSectionsProvider] = useState<ExtensionProvider | null>(null);

	const intl = useIntl();

	const hubExtensionHandlers = useHubExtensionHandlers({
		contentId,
		PropsProvider,
	});

	const allExtensionHandlers = useMemo(() => {
		return { ...hubExtensionHandlers, ...additionalExtensionHandlers };
	}, [hubExtensionHandlers, additionalExtensionHandlers]);

	useEffect(() => {
		const loadSectionsProvider = async () => {
			const defaultExtensionProviderProps = {
				contentId,
				propsProvider: PropsProvider,
				mode: ExtensionMode.RENDERER,
				getSectionExtensionContents,
			};

			const defaultExtensionInstanceProps = {
				intl,
				contentId,
			};

			const sectionsProviderInstance = await sectionExtensionProvider(
				defaultExtensionProviderProps,
			);
			const sectionsProvider = await sectionsProviderInstance(defaultExtensionInstanceProps);
			setSectionsProvider(sectionsProvider);
		};

		loadSectionsProvider();
	}, [contentId, intl, PropsProvider]);

	const providerFactory = useMemo(() => {
		const factory = dataProviders || ProviderFactory.create({});
		if (sectionsProvider) {
			factory.setProvider(
				'extensionProvider',
				Promise.resolve(combineExtensionProviders([sectionsProvider])),
			);
		}
		return factory;
	}, [sectionsProvider, dataProviders]);

	const smartCardClient = useMemo(() => new CardClient(), []);

	return (
		<HubRendererWrapper data-testid="hub-renderer">
			<SmartCardProvider product={product} client={smartCardClient}>
				<AkRenderer
					{...props}
					allowCustomPanels={true}
					media={{
						allowLinking: true,
					}}
					extensionHandlers={allExtensionHandlers}
					// dataProviders supplies the extension provider factory which enables section extensions to be rendered within the hub
					dataProviders={providerFactory}
					adfStage="stage0"
				/>
			</SmartCardProvider>
		</HubRendererWrapper>
	);
};

export default HubRenderer;
